import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Eye,
  Trash2,
  Calendar,
  MapPin,
  Clock,
  X,
  DollarSign,
  Mail,
  Phone,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mic2,
  Users,
  FileText,
  Send,
  ExternalLink,
  Edit,
  Loader,
} from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { createContract, getContractByBookingId, updateContractStatus, CONTRACT_STATUS } from '../../services/contractService';
import ContractEditModal from '../../components/ui/ContractEditModal';

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [existingContract, setExistingContract] = useState(null);
  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [, setIsSendingEmail] = useState(false);

  // Check if booking already has a contract
  useEffect(() => {
    const checkContract = async () => {
      if (selectedBooking?.id) {
        try {
          const contract = await getContractByBookingId(selectedBooking.id);
          setExistingContract(contract);
        } catch {
          setExistingContract(null);
        }
      } else {
        setExistingContract(null);
      }
    };
    checkContract();
  }, [selectedBooking?.id]);

  const handleCreateContract = async () => {
    if (!selectedBooking) return;

    setIsCreatingContract(true);
    try {
      const contract = await createContract(selectedBooking.id, selectedBooking);
      setExistingContract(contract);
      setShowContractModal(true);
    } catch (error) {
      console.error('Error creating contract:', error);
      alert('Failed to create contract');
    } finally {
      setIsCreatingContract(false);
    }
  };

  const handleSaveDraft = async (contractData) => {
    if (!existingContract) return;

    try {
      // Update contract in Firestore
      await updateDoc(doc(db, 'contracts', existingContract.id), {
        clientName: contractData.clientName,
        clientEmail: contractData.clientEmail,
        clientPhone: contractData.clientPhone,
        organization: contractData.organization,
        eventDate: contractData.eventDate,
        eventTime: contractData.eventTime,
        eventEndTime: contractData.eventEndTime || null,
        eventType: contractData.eventType,
        venue: contractData.venue,
        venueAddress: contractData.venueAddress,
        guestCount: contractData.guestCount,
        artistId: contractData.artistId,
        artistName: contractData.artistName,
        packageId: contractData.packageId,
        packageName: contractData.packageName,
        packageDuration: contractData.packageDuration,
        packagePrice: contractData.packagePrice,
        depositAmount: contractData.depositAmount,
        totalAmount: contractData.totalAmount,
        secondPaymentAmount: contractData.secondPaymentAmount || null,
        secondPaymentDue: contractData.secondPaymentDue || null,
        loadInTime: contractData.loadInTime || null,
        soundCheckTime: contractData.soundCheckTime || null,
        addendum: contractData.addendum || null,
        requiresSound: contractData.requiresSound,
        soundBacklineCost: contractData.soundBacklineCost,
      });

      // Sync updated details back to the booking record
      if (existingContract.bookingId) {
        try {
          await updateDoc(doc(db, 'bookings', existingContract.bookingId), {
            contactName: contractData.clientName,
            contactEmail: contractData.clientEmail,
            contactPhone: contractData.clientPhone,
            organization: contractData.organization,
            eventDate: contractData.eventDate,
            eventTime: contractData.eventTime,
            eventType: contractData.eventType,
            venue: contractData.venue,
            guestCount: contractData.guestCount,
          });
        } catch (bookingErr) {
          console.warn('Could not update booking record:', bookingErr);
          // Don't fail the whole operation if booking update fails
        }
      }

      setExistingContract({ ...existingContract, ...contractData });
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  };

  const handleSendContract = async (contractData) => {
    if (!selectedBooking || !existingContract) return;

    setIsSendingEmail(true);
    try {
      // First, update the contract in Firestore with the edited data
      // This ensures the customer sees the same contract we edited
      await updateDoc(doc(db, 'contracts', existingContract.id), {
        clientName: contractData.clientName,
        clientEmail: contractData.clientEmail,
        clientPhone: contractData.clientPhone,
        organization: contractData.organization,
        eventDate: contractData.eventDate,
        eventTime: contractData.eventTime,
        eventEndTime: contractData.eventEndTime || null,
        eventType: contractData.eventType,
        venue: contractData.venue,
        venueAddress: contractData.venueAddress,
        guestCount: contractData.guestCount,
        artistId: contractData.artistId,
        artistName: contractData.artistName,
        packageId: contractData.packageId,
        packageName: contractData.packageName,
        packageDuration: contractData.packageDuration,
        packagePrice: contractData.packagePrice,
        depositAmount: contractData.depositAmount,
        totalAmount: contractData.totalAmount,
        secondPaymentAmount: contractData.secondPaymentAmount || null,
        secondPaymentDue: contractData.secondPaymentDue || null,
        loadInTime: contractData.loadInTime || null,
        soundCheckTime: contractData.soundCheckTime || null,
        requiresSound: contractData.requiresSound || 'no',
        soundBacklineCost: contractData.soundBacklineCost || 0,
        addendum: contractData.addendum || null,
      });

      // Sync updated details back to the booking record
      if (existingContract.bookingId) {
        try {
          await updateDoc(doc(db, 'bookings', existingContract.bookingId), {
            contactName: contractData.clientName,
            contactEmail: contractData.clientEmail,
            contactPhone: contractData.clientPhone,
            organization: contractData.organization,
            eventDate: contractData.eventDate,
            eventTime: contractData.eventTime,
            eventType: contractData.eventType,
            venue: contractData.venue,
            guestCount: contractData.guestCount,
          });
        } catch (bookingErr) {
          console.warn('Could not update booking record:', bookingErr);
          // Don't fail the whole operation if booking update fails
        }
      }

      // Create payment link with the edited contract data
      const paymentLinkRes = await fetch('/api/create-payment-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contractId: existingContract.id,
          artistName: contractData.artistName,
          packageName: contractData.packageName,
          eventDate: contractData.eventDate,
          depositAmount: contractData.depositAmount,
          clientEmail: contractData.clientEmail,
        }),
      });

      const paymentData = await paymentLinkRes.json();

      // Check if API call was successful
      if (!paymentLinkRes.ok || !paymentData.success || !paymentData.paymentUrl) {
        console.error('Payment link API error:', paymentData);
        throw new Error(paymentData.error || 'Failed to create payment link. Please check Stripe configuration.');
      }

      const paymentLink = paymentData.paymentUrl;

      // Send email with contract and payment link using edited data
      const emailRes = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: contractData.clientEmail,
          subject: `Your Performance Agreement - ${contractData.artistName}`,
          type: 'contract-ready',
          customerName: contractData.clientName,
          artistName: contractData.artistName,
          venue: contractData.venue,
          eventDate: contractData.eventDate,
          contractLink: `${window.location.origin}/sign-contract/${existingContract.id}`,
          paymentLink: paymentLink,
        }),
      });

      const emailResult = await emailRes.json();

      if (emailResult.success) {
        // Update contract status to sent
        await updateContractStatus(existingContract.id, CONTRACT_STATUS.SENT, {
          sentViaEmail: true,
          sentToEmail: contractData.clientEmail,
          stripePaymentLink: paymentLink,
        });

        alert('Contract sent to customer successfully!');
        setShowContractModal(false);
        setSelectedBooking(null);
      } else {
        throw new Error(emailResult.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending contract:', error);
      throw error;
    } finally {
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookingsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBookings(bookingsData);
      // Keep selectedBooking in sync when Firestore updates (e.g. after contract edits)
      setSelectedBooking((prev) => {
        if (!prev) return prev;
        const updated = bookingsData.find((b) => b.id === prev.id);
        return updated || prev;
      });
    });

    return unsubscribe;
  }, []);

  const handleStatusUpdate = async (bookingId, newStatus) => {
    setIsLoading(true);
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: newStatus,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('Failed to update booking status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      await deleteDoc(doc(db, 'bookings', id));
      setSelectedBooking(null);
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Failed to delete booking');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'confirmed':
        return 'bg-green-500';
      case 'declined':
        return 'bg-red-500';
      case 'completed':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <AlertCircle className="w-4 h-4" />;
      case 'confirmed':
        return <CheckCircle className="w-4 h-4" />;
      case 'declined':
        return <XCircle className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    // Parse YYYY-MM-DD as local time to avoid UTC off-by-one
    const date = /^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))
      ? (() => { const [y,m,d] = String(dateStr).split('-').map(Number); return new Date(y, m-1, d); })()
      : new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'TBA';
    // If already in 12h format (contains AM/PM), return as-is
    if (/AM|PM/i.test(timeStr)) return timeStr;
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const stats = {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === 'pending').length,
    confirmed: bookings.filter((b) => b.status === 'confirmed').length,
    declined: bookings.filter((b) => b.status === 'declined').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Booking Requests</h2>
        <p className="text-gray-400">Manage event booking inquiries</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          className={`cursor-pointer transition-all ${filter === 'all' ? 'border-orange-500' : ''}`}
          onClick={() => setFilter('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Mic2 className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-gray-400">Total Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filter === 'pending' ? 'border-yellow-500' : ''}`}
          onClick={() => setFilter('pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending}</p>
                <p className="text-xs text-gray-400">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filter === 'confirmed' ? 'border-green-500' : ''}`}
          onClick={() => setFilter('confirmed')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.confirmed}</p>
                <p className="text-xs text-gray-400">Confirmed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filter === 'declined' ? 'border-red-500' : ''}`}
          onClick={() => setFilter('declined')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.declined}</p>
                <p className="text-xs text-gray-400">Declined</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.map((booking) => (
          <motion.div
            key={booking.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="hover:border-orange-500/50 transition-all">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {/* Date Box */}
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-3 text-center min-w-[70px]">
                      <div className="text-2xl font-bold text-white">
                        {booking.eventDate
                          ? (() => { const [y,m,d] = booking.eventDate.split('-').map(Number); return new Date(y, m-1, d).getDate(); })()
                          : '??'}
                      </div>
                      <div className="text-xs text-gray-400 uppercase">
                        {booking.eventDate
                          ? (() => { const [y,m,d] = booking.eventDate.split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString('en-US', { month: 'short' }); })()
                          : 'TBA'}
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-white">
                          {booking.artist === 'scooby' ? 'Scooby DaGodSon' : 'Sirius Company'}
                        </h3>
                        <span
                          className={`px-2 py-0.5 text-xs font-semibold rounded capitalize text-white flex items-center gap-1 ${getStatusColor(
                            booking.status
                          )}`}
                        >
                          {getStatusIcon(booking.status)}
                          {booking.status || 'pending'}
                        </span>
                      </div>
                      <p className="text-orange-400 font-medium capitalize">
                        {booking.eventType?.replace('_', ' ')} - {booking.package}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {booking.contactName}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {booking.venue || 'TBA'}
                        </span>
                        {booking.packageName && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {booking.packageName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 md:flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedBooking(booking)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    {booking.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleStatusUpdate(booking.id, 'confirmed')}
                          className="bg-green-500 hover:bg-green-600"
                          disabled={isLoading}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleStatusUpdate(booking.id, 'declined')}
                          className="bg-red-500 hover:bg-red-600"
                          disabled={isLoading}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {filteredBookings.length === 0 && (
          <div className="text-center py-12">
            <Mic2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No bookings found</h3>
            <p className="text-gray-400">
              {filter === 'all'
                ? 'Booking requests will appear here'
                : `No ${filter} bookings`}
            </p>
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      <AnimatePresence>
        {selectedBooking && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Booking Details</h3>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded capitalize text-white mt-1 ${getStatusColor(
                    selectedBooking.status
                  )}`}
                >
                  {getStatusIcon(selectedBooking.status)}
                  {selectedBooking.status || 'pending'}
                </span>
              </div>
              <button
                onClick={() => setSelectedBooking(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Contact Information</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <User className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Name</p>
                      <p className="text-white font-medium">{selectedBooking.contactName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Mail className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Email</p>
                      <a href={`mailto:${selectedBooking.contactEmail}`} className="text-white font-medium hover:text-orange-400">
                        {selectedBooking.contactEmail}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Phone className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Phone</p>
                      <a href={`tel:${selectedBooking.contactPhone}`} className="text-white font-medium hover:text-orange-400">
                        {selectedBooking.contactPhone || 'Not provided'}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Users className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Organization</p>
                      <p className="text-white font-medium">{selectedBooking.organization || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Event Details</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Mic2 className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Artist</p>
                      <p className="text-white font-medium">
                        {selectedBooking.artist === 'scooby' ? 'Scooby DaGodSon' : 'Sirius Company'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <FileText className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Event Type</p>
                      <p className="text-white font-medium capitalize">
                        {selectedBooking.eventType?.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Event Date</p>
                      <p className="text-white font-medium">{formatDate(selectedBooking.eventDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Event Time</p>
                      <p className="text-white font-medium">{formatTime(selectedBooking.eventTime)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <MapPin className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Venue</p>
                      <p className="text-white font-medium">{selectedBooking.venue || 'TBA'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <DollarSign className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Package</p>
                      <p className="text-white font-medium">{selectedBooking.packageName || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Users className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Guest Count</p>
                      <p className="text-white font-medium">{selectedBooking.guestCount || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-400">Duration</p>
                      <p className="text-white font-medium">{selectedBooking.packageDuration || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Details */}
              {(selectedBooking.address || selectedBooking.city || selectedBooking.state) && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Location Details</h4>
                  <div className="p-4 bg-gray-800 rounded-lg">
                    <p className="text-white font-medium">
                      {selectedBooking.address && <span>{selectedBooking.address}<br /></span>}
                      {selectedBooking.city && selectedBooking.state
                        ? `${selectedBooking.city}, ${selectedBooking.state}`
                        : selectedBooking.city || selectedBooking.state || ''}
                    </p>
                  </div>
                </div>
              )}

              {/* Package */}
              {selectedBooking.package && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Selected Package</h4>
                  <div className="p-4 bg-gradient-to-br from-orange-500/10 to-red-600/10 border border-orange-500/30 rounded-lg">
                    <p className="text-white font-bold capitalize">{selectedBooking.package}</p>
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {selectedBooking.message && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Additional Notes</h4>
                  <div className="p-4 bg-gray-800 rounded-lg">
                    <p className="text-gray-300 whitespace-pre-wrap">{selectedBooking.message}</p>
                  </div>
                </div>
              )}

              {/* Contract Section */}
              {selectedBooking.status === 'confirmed' && (
                <div className="pt-4 border-t border-gray-800">
                  <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Contract Management</h4>
                  {existingContract ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-white font-medium">Contract Created</p>
                          <p className="text-sm text-gray-400">
                            Status: <span className="capitalize">{existingContract.status}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/sign-contract/${existingContract.id}`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setShowContractModal(true)}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit Contract
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            const link = `${window.location.origin}/sign-contract/${existingContract.id}`;
                            navigator.clipboard.writeText(link);
                            alert('Contract link copied!');
                          }}
                          variant="outline"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      onClick={handleCreateContract}
                      disabled={isCreatingContract}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                    >
                      {isCreatingContract ? (
                        <div className="flex items-center gap-2">
                          <Loader className="w-4 h-4 animate-spin" />
                          Generating Contract...
                        </div>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Generate Contract
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-gray-800">
                {selectedBooking.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => {
                        handleStatusUpdate(selectedBooking.id, 'confirmed');
                        setSelectedBooking({ ...selectedBooking, status: 'confirmed' });
                      }}
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      disabled={isLoading}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirm Booking
                    </Button>
                    <Button
                      onClick={() => {
                        handleStatusUpdate(selectedBooking.id, 'declined');
                        setSelectedBooking({ ...selectedBooking, status: 'declined' });
                      }}
                      variant="outline"
                      className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      disabled={isLoading}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={() => handleDelete(selectedBooking.id)}
                  className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      </AnimatePresence>

      {/* Contract Edit Modal */}
      <AnimatePresence>
        {showContractModal && selectedBooking && existingContract && (
          <ContractEditModal
            booking={selectedBooking}
            contract={existingContract}
            onClose={() => setShowContractModal(false)}
            onSend={handleSendContract}
            onSaveDraft={handleSaveDraft}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
